import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTrigger } from "@/src/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@radix-ui/react-separator";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { useEmit } from "@/src/lib/actions";
import { useState } from "react";

const schema = z.object({
  customerName: z.string().optional(),
  customerEmail: z.string().optional(),
  customer: z
    .object({ id: z.number(), firstName: z.string(), lastName: z.string(), email: z.string() })
    .optional()
    .nullable(),
});
export type FiltersInput = z.infer<typeof schema>;

export function FiltersButton() {
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const form = useForm<FiltersInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      customerName: undefined,
      customerEmail: undefined,
    },
  });

  const emitCustomerName = useEmit<string | undefined>("payments:filter-customer-name");
  const emitCustomerEmail = useEmit<string | undefined>("payments:filter-customer-email");

  const onSubmit: SubmitHandler<FiltersInput> = async (data) => {
    try {
      const customerName = data?.customerName;
      const customerEmail = data?.customerEmail;

      if (customerName || customerName === undefined || customerName === "") emitCustomerName(data?.customerName);
      if (customerEmail || customerEmail === undefined || customerEmail === "") emitCustomerEmail(data?.customerEmail);

      setOpen(false);
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const resetForm = () => {
    emitCustomerName(undefined);
    emitCustomerEmail(undefined);
    form.reset();
    setResetKey((prevKey) => prevKey + 1);
  };

  const onClearAll = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => setOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="action">Filters</Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-xl"
        style={{
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <DialogHeader>
          <h2 className="text-3xl">Filters</h2>
        </DialogHeader>
        <Separator />
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} key={resetKey}>
            <div className="flex items-start gap-4">
              <FormField<FiltersInput, "customerEmail">
                name="customerEmail"
                render={({ field }) => (
                  <FormItem className="flex-grow w-1/2">
                    <FormLabel className="text-base">E-mail</FormLabel>
                    <FormControl>
                      <Input placeholder="Search by email..." {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex-grow w-1/2"></div>
            </div>

            <div className="flex items-start gap-4">
              <FormField<FiltersInput, "customerName">
                name="customerName"
                render={({ field }) => (
                  <FormItem className="flex-grow w-1/2">
                    <FormLabel className="text-base ">Customer</FormLabel>
                    <FormControl>
                      <Input placeholder="Search for customer..." {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex-grow w-1/2"></div>
            </div>

            <div className="-mx-6 mt-2">
              <hr className="border-t border-gray-200 w-full" />
            </div>
            <div className="flex justify-start items-center gap-2">
              <Button type="button" className="mr-auto" variant="link" onClick={(e) => onClearAll(e)}>
                Clear all
              </Button>
              <Button type="submit">Show customers</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
